import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { ListingCard } from "@/components/ListingCard";
import { StarRating } from "@/components/StarRating";
import { SellerBadge } from "@/components/SellerBadge";
import { RstScoreBadge } from "@/components/RstScoreBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ShieldCheck, MapPin, ArrowLeft, Lock } from "lucide-react";

export default function SellerProfile() {
  const { id } = useParams();
  const { t } = useT();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/sellers/${id}`).then((r) => setData(r.data)).catch(() => navigate("/catalogue")).finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return <div className="container-page py-10"><Skeleton className="h-40 rounded-2xl" /></div>;
  if (!data) return null;

  return (
    <div className="container-page py-10">
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 -ml-2 text-muted-foreground hover:text-primary">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> {t("common.back")}
      </Button>

      <div className="rounded-2xl bg-secondary text-secondary-foreground p-6 sm:p-8" data-testid="seller-profile">
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <div>
            <h1 className="font-heading font-extrabold text-3xl flex items-center gap-2">
              {data.name}
              {data.verified && <ShieldCheck className="h-6 w-6 text-accent" />}
            </h1>
            <p className="text-secondary-foreground/75 mt-1 flex items-center gap-1.5">
              <MapPin className="h-4 w-4" /> {data.city ? `${data.city}, ` : ""}{data.region || t("hero.badge")}
            </p>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <StarRating value={data.rating_avg} count={data.rating_count} size={18} />
              <SellerBadge badge={data.badge} premium={data.premium} size="md" />
              <RstScoreBadge score={data.rst_score} tier={data.rst_tier} size="md" light />
            </div>
          </div>
          <div className="text-right">
            <p className="font-heading font-extrabold text-4xl">{data.listings.length}</p>
            <p className="text-secondary-foreground/75 text-sm">{t("seller.listings")}</p>
          </div>
        </div>
        <p className="mt-4 text-xs text-secondary-foreground/60 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> {t("seller.contactHidden")}</p>
      </div>

      {data.reviews.length > 0 && (
        <section className="mt-10">
          <h2 className="font-heading font-bold text-2xl text-secondary mb-4">{t("review.reviews")} ({data.rating_count})</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {data.reviews.map((r) => (
              <div key={r.id} className="rounded-2xl bg-card border border-border p-4" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-secondary">{r.buyer_name}</p>
                  <StarRating value={r.rating} size={14} />
                </div>
                {r.comment && <p className="text-sm text-muted-foreground mt-2">{r.comment}</p>}
                <p className="text-xs text-secondary mt-2 flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> {t("review.verified")}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mt-10">
        <h2 className="font-heading font-bold text-2xl text-secondary mb-4">{t("seller.listings")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {data.listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
        </div>
      </section>
    </div>
  );
}
